import React from 'react';
import { Link } from 'react-router-dom';
import { BackButton } from '../../components/ui/BackButton';

const EFFECTIVE_DATE = 'July 21, 2026';
const LAST_UPDATED = 'July 21, 2026';

interface SectionProps {
  id: string;
  number: string;
  title: string;
  children: React.ReactNode;
}

function Section({ id, number, title, children }: SectionProps) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-lg font-serif text-heading mb-3">
        {number}. {title}
      </h2>
      <div className="space-y-3 text-sm text-body leading-relaxed">{children}</div>
    </section>
  );
}

function Sub({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-medium text-heading mb-1.5">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

export function MerchantAgreementPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center">
            <BackButton fallback="/business/settings" />
            <h1 className="ml-4 text-xl font-serif text-heading">Merchant Agreement</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 pb-24">
        <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-8 space-y-8">
          <div className="border-b border-gray-100 pb-6">
            <p className="text-4xl font-serif text-primary mb-2">Fash</p>
            <h2 className="text-2xl font-serif text-heading">Merchant Agreement</h2>
            <p className="text-sm text-body mt-3">
              Effective Date: {EFFECTIVE_DATE}
              <span className="mx-2 text-gray-300">·</span>
              Last Updated: {LAST_UPDATED}
            </p>
            <p className="text-sm text-body mt-4 leading-relaxed">
              This Merchant Agreement (&quot;Agreement&quot;) is a binding contract between Fash
              (&quot;Fash,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;Platform&quot;) and the business entity or individual
              that activates a boutique on Fash (&quot;Merchant,&quot; &quot;you,&quot; or &quot;your&quot;). By checking
              the acceptance box during onboarding, activating your boutique, or continuing to
              use the Platform as a merchant, you agree to these terms.
            </p>
          </div>

          <nav className="bg-gray-50 rounded-xl p-4 text-sm">
            <p className="font-medium text-heading mb-2">Contents</p>
            <ol className="columns-1 sm:columns-2 gap-6 space-y-1 text-body list-decimal list-inside">
              <li><a href="#parties" className="hover:text-primary">Parties &amp; Acceptance</a></li>
              <li><a href="#eligibility" className="hover:text-primary">Eligibility</a></li>
              <li><a href="#account" className="hover:text-primary">Account &amp; Boutique Profile</a></li>
              <li><a href="#listings" className="hover:text-primary">Products &amp; Inventory</a></li>
              <li><a href="#orders" className="hover:text-primary">Orders &amp; Fulfillment</a></li>
              <li><a href="#delivery" className="hover:text-primary">Delivery &amp; Pickup</a></li>
              <li><a href="#fees" className="hover:text-primary">Fees &amp; Pricing</a></li>
              <li><a href="#payments" className="hover:text-primary">Payments &amp; Payouts</a></li>
              <li><a href="#returns" className="hover:text-primary">Returns, Refunds &amp; Disputes</a></li>
              <li><a href="#taxes" className="hover:text-primary">Taxes &amp; Compliance</a></li>
              <li><a href="#ip" className="hover:text-primary">Intellectual Property</a></li>
              <li><a href="#conduct" className="hover:text-primary">Prohibited Conduct</a></li>
              <li><a href="#data" className="hover:text-primary">Data &amp; Privacy</a></li>
              <li><a href="#suspension" className="hover:text-primary">Suspension &amp; Termination</a></li>
              <li><a href="#liability" className="hover:text-primary">Liability &amp; Indemnity</a></li>
              <li><a href="#general" className="hover:text-primary">General Terms</a></li>
            </ol>
          </nav>

          <Section id="parties" number="1" title="Parties & Acceptance">
            <p>
              Fash operates a marketplace that connects shoppers with independent local boutiques
              for browsing, purchasing, and same-day or scheduled delivery of fashion and related
              goods. You are the seller of record for all products you list. Fash is not the buyer
              or seller of your merchandise except where we expressly state otherwise.
            </p>
            <p>
              If you accept this Agreement on behalf of a company, LLC, partnership, or other
              entity, you represent that you have authority to bind that entity. If you do not
              have such authority, do not activate a boutique.
            </p>
            <p>
              This Agreement incorporates by reference Fash&apos;s Privacy Policy, any applicable
              shopper Terms of Service, and any fee schedule or program terms we publish in your
              business dashboard or communicate to you in writing.
            </p>
          </Section>

          <Section id="eligibility" number="2" title="Eligibility & Onboarding">
            <Sub title="2.1 Who may join">
              <p>To operate as a Merchant on Fash you must:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Be at least 18 years old and legally able to enter contracts;</li>
                <li>Operate a legitimate retail or boutique business with authority to sell the goods you list;</li>
                <li>Provide accurate legal business information, tax identification, and authorized representative details;</li>
                <li>Complete required onboarding steps, including identity, location, hours, fulfillment preferences, and payout setup;</li>
                <li>Maintain a valid Stripe Connected Account (or other payment account we designate) with charges and payouts enabled as required.</li>
              </ul>
            </Sub>
            <Sub title="2.2 Verification">
              <p>
                Fash may verify your business, identity, inventory practices, and compliance at any
                time. We may request additional documents (for example, proof of address, resale
                certificate, or product authenticity documentation). Failure to provide requested
                information within a reasonable time may result in suspension or denial of listing.
              </p>
            </Sub>
            <Sub title="2.3 No guarantee of acceptance">
              <p>
                Completing onboarding does not guarantee continued access. Fash may approve,
                reject, limit, or condition marketplace participation at its reasonable discretion
                to protect shoppers, partners, and platform integrity.
              </p>
            </Sub>
          </Section>

          <Section id="account" number="3" title="Account & Boutique Profile">
            <Sub title="3.1 Accuracy">
              <p>
                You are responsible for keeping your boutique profile accurate and current,
                including store name, branding, address, contact details, hours of operation,
                fulfillment settings, return policy, and team access. Misleading profiles or
                &quot;bait&quot; listings are prohibited.
              </p>
            </Sub>
            <Sub title="3.2 Credentials & access">
              <p>
                You must safeguard login credentials and limit dashboard access to authorized
                personnel. You are responsible for all activity under your account. Notify
                Fash promptly at{' '}
                <a href="mailto:support@fashapp.com" className="text-primary hover:underline">
                  support@fashapp.com
                </a>{' '}
                if you suspect unauthorized access.
              </p>
            </Sub>
            <Sub title="3.3 Store readiness">
              <p>
                When your boutique is marked active or open for orders, you represent that you
                are prepared to accept, prepare, and hand off orders according to your published
                hours and fulfillment settings.
              </p>
            </Sub>
          </Section>

          <Section id="listings" number="4" title="Products, Pricing & Inventory">
            <Sub title="4.1 Listing standards">
              <p>For each product you list, you must provide:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Clear, accurate titles, descriptions, sizes, colors, and condition;</li>
                <li>Truthful pricing, including sale or promotional pricing when marked as such;</li>
                <li>Images that fairly represent the actual item offered;</li>
                <li>Available inventory quantities you can fulfill;</li>
                <li>Any material limitations (e.g., final sale, limited stock, pre-order timing).</li>
              </ul>
            </Sub>
            <Sub title="4.2 Authenticity & rights">
              <p>
                You represent and warrant that you own or have all rights necessary to sell each
                listed item; that items are authentic unless clearly disclosed otherwise; and that
                listings do not infringe trademarks, copyrights, publicity rights, or other third-party
                rights. Counterfeit, stolen, recalled, or illegally imported goods are strictly
                prohibited.
              </p>
            </Sub>
            <Sub title="4.3 Inventory accuracy">
              <p>
                You must maintain inventory that reflects real availability. Repeated cancellations
                due to stockouts, phantom inventory, or failure to update listings may result in
                ranking penalties, temporary pause of listings, or removal from the Platform.
              </p>
            </Sub>
            <Sub title="4.4 Pricing control">
              <p>
                You set retail prices for your goods. Fash does not control your retail prices but
                may display estimated totals that include delivery fees, tips, taxes, and platform
                service charges charged to shoppers where applicable.
              </p>
            </Sub>
            <Sub title="4.5 Integrations">
              <p>
                If you connect Shopify or another inventory source, you remain responsible for sync
                accuracy, mapping of variants/SKUs, and resolving mismatches between external
                systems and Fash listings.
              </p>
            </Sub>
          </Section>

          <Section id="orders" number="5" title="Orders & Fulfillment Obligations">
            <Sub title="5.1 Order acceptance">
              <p>
                When a shopper places an order for your boutique, you must promptly review and
                confirm or decline according to Platform workflows and your operating hours.
                Unreasonable delays in confirmation or preparation harm shoppers and may trigger
                automatic cancellation, refunds, or account review.
              </p>
            </Sub>
            <Sub title="5.2 Preparation standards">
              <p>You agree to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Prepare the correct items, sizes, and quantities as ordered;</li>
                <li>Package goods securely and presentably for handoff;</li>
                <li>Make orders available for courier pickup or shopper pickup within the expected window;</li>
                <li>Communicate material issues (out of stock, delay) through Platform tools as soon as practicable.</li>
              </ul>
            </Sub>
            <Sub title="5.3 Substitutions">
              <p>
                Do not substitute items without shopper consent through approved Platform channels.
                Unauthorized substitutions may be treated as a fulfillment failure and may entitle
                the shopper to a refund or remake at your expense.
              </p>
            </Sub>
            <Sub title="5.4 Seller of record">
              <p>
                You are the merchant of record for the sale of goods. Contractual sale terms with
                the shopper for the merchandise (including your return policy as published) are
                between you and the shopper, subject to this Agreement and Platform dispute rules.
              </p>
            </Sub>
          </Section>

          <Section id="delivery" number="6" title="Delivery, Pickup & Couriers">
            <Sub title="6.1 Delivery partners">
              <p>
                Fash may arrange delivery through third-party couriers (including partners such as
                Uber Direct or Platform-managed drivers) or support in-store pickup, depending on
                your settings and service availability. Courier services are provided by third
                parties under their own terms; Fash does not guarantee courier arrival times.
              </p>
            </Sub>
            <Sub title="6.2 Merchant handoff">
              <p>
                You must make the package available to the assigned courier at the designated
                pickup location and time. Failure to hand off may result in failed delivery fees,
                order cancellation, and chargebacks or refunds charged against amounts otherwise
                owed to you.
              </p>
            </Sub>
            <Sub title="6.3 Risk of loss">
              <p>
                Risk of loss for merchandise generally passes according to the fulfillment method:
                for courier delivery, after verified handoff to the courier; for pickup, upon
                verified transfer to the shopper. This does not limit your responsibility for
                shipping incorrect, damaged-at-handoff, or defective goods.
              </p>
            </Sub>
            <Sub title="6.4 Restricted delivery items">
              <p>
                You may not request delivery of items that couriers or law prohibit (hazardous
                materials, weapons, illegal goods, etc.). Fash and courier partners may refuse such
                deliveries.
              </p>
            </Sub>
          </Section>

          <Section id="fees" number="7" title="Fees & Pricing Structure">
            <Sub title="7.1 Platform fee">
              <p>
                In consideration for marketplace, payment, and related services, Fash charges a
                platform application fee on eligible order amounts. Unless a different rate is
                shown in your dashboard or agreed in a separate written program (for example,
                a launch or promotional commission), the standard platform fee is{' '}
                <strong className="text-heading">ten percent (10%)</strong> of the applicable
                merchandise subtotal (after merchant-funded discounts or promo reductions that
                Fash designates as reducing the fee base), as calculated by the Platform at
                checkout and settlement.
              </p>
            </Sub>
            <Sub title="7.2 Delivery and related amounts">
              <p>
                Delivery fees, tips, and certain shopper-facing service charges may be collected
                from the shopper and remitted or allocated according to Platform payment flows.
                Delivery fees are generally not merchant revenue. Tip amounts intended for couriers
                are passed through to couriers and are not retained by you.
              </p>
            </Sub>
            <Sub title="7.3 Payment processor fees">
              <p>
                Stripe (or another processor) may charge processing, payout, dispute, or currency
                fees under its Connected Account terms. Those fees are your responsibility unless
                Fash expressly absorbs them in a written promotion.
              </p>
            </Sub>
            <Sub title="7.4 Fee changes">
              <p>
                Fash may change fees with notice via email, dashboard banner, or in-product notice.
                Continued use of the Platform after the effective date of a fee change constitutes
                acceptance. If you do not agree, you must deactivate your boutique before the
                change takes effect.
              </p>
            </Sub>
            <Sub title="7.5 Promotions">
              <p>
                Platform-funded promotions may be reimbursed according to program rules. Merchant-funded
                discounts reduce amounts payable to you. You must honor promo codes and sale prices
                you authorize.
              </p>
            </Sub>
          </Section>

          <Section id="payments" number="8" title="Payments, Payouts & Chargebacks">
            <Sub title="8.1 Stripe Connect">
              <p>
                Payouts are processed through Stripe Connect (or a successor processor). You must
                complete Connect onboarding, keep bank details current, and comply with Stripe&apos;s
                Connected Account Agreement and applicable financial partner requirements.
              </p>
            </Sub>
            <Sub title="8.2 Settlement">
              <p>
                For each successful charge, Fash (as platform) may collect an application fee and
                related amounts. Net proceeds due to you are paid to your Connected Account subject
                to holds, reserves, offsets, refunds, and dispute adjustments.
              </p>
            </Sub>
            <Sub title="8.3 Holds & offsets">
              <p>
                Fash may delay, withhold, or offset payouts to cover refunds, chargebacks, failed
                deliveries attributable to you, suspected fraud, policy violations, or amounts you
                owe Fash. We may establish reserves where risk warrants.
              </p>
            </Sub>
            <Sub title="8.4 Chargebacks & payment disputes">
              <p>
                You must reasonably cooperate in responding to payment network disputes. Losses,
                fees, and penalties arising from chargebacks tied to your merchandise, fulfillment,
                or customer service may be charged to you. Repeated chargebacks are grounds for
                suspension.
              </p>
            </Sub>
            <Sub title="8.5 No interest">
              <p>
                Amounts held or pending payout do not accrue interest. Timing of payouts depends on
                Stripe schedules, bank rails, and any Platform risk controls.
              </p>
            </Sub>
          </Section>

          <Section id="returns" number="9" title="Returns, Refunds & Disputes">
            <Sub title="9.1 Your return policy">
              <p>
                You must publish an accurate return policy during onboarding and in settings (for
                example: full refund, exchange only, store credit, custom, or all sales final) and
                honor that policy for eligible orders, subject to law and Platform dispute rules.
              </p>
            </Sub>
            <Sub title="9.2 Platform dispute process">
              <p>
                Shoppers may open disputes through Fash for issues such as undelivered items,
                wrong item, damaged goods, or quality problems. You agree to respond promptly with
                evidence (photos, packing records, courier handoff confirmation). Fash may mediate
                and issue a determination that is final as between you and the shopper for Platform
                purposes.
              </p>
            </Sub>
            <Sub title="9.3 Refunds">
              <p>
                Refunds, partial refunds, or remakes may be issued when required by your policy,
                applicable law, payment network rules, or a Platform dispute decision. Refunded
                amounts and associated fees may be deducted from future payouts or invoiced to you.
              </p>
            </Sub>
            <Sub title="9.4 Merchant fault">
              <p>
                Where Fash reasonably determines an issue was caused by incorrect listing,
                incorrect pick, packaging failure, or refusal to honor a valid policy, you bear
                the cost of the remedy, which may include refund of merchandise and related fees.
              </p>
            </Sub>
          </Section>

          <Section id="taxes" number="10" title="Taxes, Records & Regulatory Compliance">
            <Sub title="10.1 Tax responsibility">
              <p>
                You are solely responsible for determining, collecting, reporting, and remitting
                all taxes arising from your sales, except where Fash is legally required to collect
                and remit on your behalf and notifies you of that treatment. Platform fee invoices
                or reports do not constitute tax advice.
              </p>
            </Sub>
            <Sub title="10.2 Information reporting">
              <p>
                You authorize Fash and its payment partners to collect tax forms (including W-9
                equivalents) and to report payments as required by law (for example, Form 1099-K
                where applicable). You must keep tax IDs and legal names accurate.
              </p>
            </Sub>
            <Sub title="10.3 Withholding pending tax documentation">
              <p>
                Fash may withhold settlement of amounts otherwise payable to you until you have
                provided complete and accurate taxpayer information, including your legal name,
                entity type, taxpayer identification number, tax address, authorized
                representative, and a signed W-9 or equivalent certification, and until our
                payment partner has enabled payouts on your connected account. Withheld amounts
                are held for your benefit, are not forfeited, and are released promptly once the
                required information is on file and verified. Fash will notify you before
                withholding begins and will identify the specific information outstanding.
                Withholding under this section does not relieve you of your obligations to
                fulfill orders already accepted.
              </p>
            </Sub>
            <Sub title="10.4 Legal compliance">
              <p>
                You must comply with all laws applicable to your business, including consumer
                protection, labeling, product safety, advertising, import, and employment rules.
                You must not use Fash to sell age-restricted goods without required controls, or
                any goods banned in your jurisdiction.
              </p>
            </Sub>
          </Section>

          <Section id="ip" number="11" title="Intellectual Property & Marketing">
            <Sub title="11.1 Your content license">
              <p>
                You grant Fash a worldwide, non-exclusive, royalty-free license to use, host,
                reproduce, display, and distribute your boutique name, logos, product images,
                descriptions, and related content for operating, promoting, and improving the
                Platform and your boutique&apos;s presence on Fash (including ads, emails, social,
                and app store creatives). You may revoke the license for specific assets by
                removing them from the Platform, subject to reasonable residual use in backups or
                historical order records.
              </p>
            </Sub>
            <Sub title="11.2 Fash brand">
              <p>
                Fash names, logos, and product designs are our property. You may not use them
                except as expressly allowed in writing or through in-product brand assets we provide.
              </p>
            </Sub>
            <Sub title="11.3 Feedback">
              <p>
                Suggestions you provide about the Platform may be used by Fash without obligation
                or compensation.
              </p>
            </Sub>
          </Section>

          <Section id="conduct" number="12" title="Prohibited Conduct">
            <p>You agree not to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>List counterfeit, stolen, hazardous, or illegal items;</li>
              <li>Manipulate reviews, ratings, search ranking, or inventory signals;</li>
              <li>Circumvent Platform fees by steering Fash shoppers to off-platform checkout for the same transaction originated on Fash;</li>
              <li>Harass shoppers, couriers, or Fash staff;</li>
              <li>Scrape, reverse engineer, or abuse APIs beyond authorized use;</li>
              <li>Create multiple accounts to evade suspension, fees, or verification;</li>
              <li>Misrepresent affiliation with brands or designers;</li>
              <li>Interfere with other merchants&apos; businesses or Platform operations.</li>
            </ul>
          </Section>

          <Section id="data" number="13" title="Data, Privacy & Confidentiality">
            <Sub title="13.1 Shopper data">
              <p>
                You may receive shopper information solely to fulfill orders and provide related
                customer service. You may not sell shopper data, use it for unrelated marketing
                without lawful consent, or retain it longer than needed for fulfillment, legal,
                or accounting purposes. You must protect personal data with reasonable security
                measures and comply with applicable privacy laws.
              </p>
            </Sub>
            <Sub title="13.2 Confidentiality">
              <p>
                Non-public Platform information (fee structures under NDA, unreleased features,
                security details, and non-public business metrics shared by Fash) is confidential
                and may not be disclosed except to employees/contractors under confidentiality
                obligations or as required by law.
              </p>
            </Sub>
          </Section>

          <Section id="suspension" number="14" title="Suspension, Termination & Wind-Down">
            <Sub title="14.1 By you">
              <p>
                You may deactivate your boutique and stop accepting new orders at any time through
                business settings, subject to completing open orders and resolving outstanding
                balances, disputes, and returns.
              </p>
            </Sub>
            <Sub title="14.2 By Fash">
              <p>
                Fash may suspend or terminate access immediately for policy violations, fraud risk,
                legal requirements, excessive cancellations/chargebacks, nonpayment, or prolonged
                inactivity. Where practicable, we will provide notice and a chance to cure for
                non-critical issues.
              </p>
            </Sub>
            <Sub title="14.3 Effect of termination">
              <p>
                Upon termination, your listings will be removed from active browsing. Provisions
                that by nature should survive (fees owed, indemnity, liability limits, IP licenses
                for historical orders, dispute handling) survive termination. Outstanding amounts
                remain due.
              </p>
            </Sub>
          </Section>

          <Section id="liability" number="15" title="Disclaimers, Liability & Indemnification">
            <Sub title="15.1 Platform provided &quot;as is&quot;">
              <p>
                THE PLATFORM AND RELATED SERVICES ARE PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE.&quot;
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, FASH DISCLAIMS WARRANTIES OF MERCHANTABILITY,
                FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT UNINTERRUPTED
                OR ERROR-FREE SERVICE, COURIER PERFORMANCE, OR ANY PARTICULAR SALES VOLUME.
              </p>
            </Sub>
            <Sub title="15.2 Limitation of liability">
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, FASH AND ITS AFFILIATES, OFFICERS, EMPLOYEES,
                AND AGENTS WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
                PUNITIVE DAMAGES, OR LOST PROFITS, REVENUE, OR DATA, ARISING FROM THIS AGREEMENT OR
                USE OF THE PLATFORM. FASH&apos;S AGGREGATE LIABILITY FOR CLAIMS RELATING TO THIS AGREEMENT
                IN ANY TWELVE-MONTH PERIOD WILL NOT EXCEED THE PLATFORM FEES YOU ACTUALLY PAID TO FASH
                IN THAT PERIOD.
              </p>
            </Sub>
            <Sub title="15.3 Indemnification">
              <p>
                You will defend, indemnify, and hold harmless Fash and its affiliates, officers,
                employees, and agents from claims, damages, losses, and expenses (including reasonable
                attorneys&apos; fees) arising out of: (a) your products, listings, or fulfillment;
                (b) your breach of this Agreement or law; (c) infringement or authenticity claims
                related to your goods or content; (d) tax obligations; or (e) disputes with shoppers
                or couriers to the extent caused by you.
              </p>
            </Sub>
            <Sub title="15.4 Independent contractors">
              <p>
                You and Fash are independent contractors. This Agreement does not create a
                partnership, joint venture, employment, or franchise relationship.
              </p>
            </Sub>
          </Section>

          <Section id="general" number="16" title="General Terms">
            <Sub title="16.1 Changes to this Agreement">
              <p>
                We may update this Agreement from time to time. Material changes will be posted
                on this page and, where appropriate, noticed in your dashboard or by email. The
                &quot;Last Updated&quot; date will change. Continued use after changes become effective
                constitutes acceptance.
              </p>
            </Sub>
            <Sub title="16.2 Governing law">
              <p>
                This Agreement is governed by the laws of the State of New York, without regard to
                conflict-of-law rules. Exclusive venue for disputes that are not resolved informally
                or by binding arbitration (if later adopted by mutual written agreement) shall be
                state or federal courts located in New York County, New York, and you consent to
                personal jurisdiction there.
              </p>
            </Sub>
            <Sub title="16.3 Notices">
              <p>
                Notices to Fash should be sent to{' '}
                <a href="mailto:support@fashapp.com" className="text-primary hover:underline">
                  support@fashapp.com
                </a>
                {' '}with a copy retained for your records. Notices to you may be sent to the email
                on your account or displayed in the business dashboard.
              </p>
            </Sub>
            <Sub title="16.4 Assignment">
              <p>
                You may not assign this Agreement without Fash&apos;s prior written consent. Fash may
                assign this Agreement in connection with a merger, acquisition, or sale of assets.
              </p>
            </Sub>
            <Sub title="16.5 Severability & waiver">
              <p>
                If a provision is unenforceable, the remainder remains in effect. Failure to
                enforce a provision is not a waiver of future enforcement.
              </p>
            </Sub>
            <Sub title="16.6 Entire agreement">
              <p>
                This Agreement, together with documents incorporated by reference, is the entire
                agreement regarding your merchant use of Fash and supersedes prior oral or written
                merchant terms on the same subject, except any separately signed enterprise addendum.
              </p>
            </Sub>
            <Sub title="16.7 Contact">
              <p>
                Questions about this Agreement:{' '}
                <a href="mailto:support@fashapp.com" className="text-primary hover:underline">
                  support@fashapp.com
                </a>
              </p>
            </Sub>
          </Section>

          <div className="border-t border-gray-100 pt-6 text-xs text-body leading-relaxed">
            <p>
              By activating your boutique and checking &quot;I agree to the Fash Merchant Agreement,&quot;
              you acknowledge that you have read, understood, and agree to be bound by this Agreement.
            </p>
            <p className="mt-4">
              <Link to="/about" className="text-primary hover:underline">
                About Fash
              </Link>
              <span className="mx-2 text-gray-300">·</span>
              <a href="mailto:support@fashapp.com" className="text-primary hover:underline">
                Contact support
              </a>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
